'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

type Props = { images: string[] };

const ImageSlider: React.FC<Props> = ({ images }) => {
    // slideIndex avanza di 1 a ogni next/prev; una slide = 2 celle
    const [slideIndex, setSlideIndex] = useState(0);
    const [direction, setDirection] = useState<'left' | 'right'>('right');
    const [loaded, setLoaded] = useState<boolean[]>([]);
    const [failed, setFailed] = useState<boolean[]>([]);

    // Normalizza input
    const srcs = useMemo(
        () => (images ?? []).map(s => (s ?? '').toString().trim()).filter(Boolean),
        // dipendenza sul contenuto reale
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [JSON.stringify(images ?? [])]
    );

    // Firma stabile per evitare loop di preload
    const signature = useMemo(() => srcs.join('|'), [srcs]);
    const prevSignatureRef = useRef<string | null>(null);

    useEffect(() => {
        if (signature === prevSignatureRef.current) return;
        prevSignatureRef.current = signature;

        let cancelled = false;
        setLoaded(Array(srcs.length).fill(false));
        setFailed(Array(srcs.length).fill(false));
        setSlideIndex(0); // reset alla prima slide quando cambia la lista

        srcs.forEach((src, i) => {
            const img = new Image();
            img.onload = () => {
                if (cancelled) return;
                setLoaded(prev => {
                    if (prev[i]) return prev;
                    const n = prev.slice(); n[i] = true; return n;
                });
            };
            img.onerror = () => {
                if (cancelled) return;
                console.warn('[ImageSlider] failed to load', i, src);
                setFailed(prev => {
                    if (prev[i]) return prev;
                    const n = prev.slice(); n[i] = true; return n;
                });
            };
            img.src = src;
        });

        return () => { cancelled = true; };
    }, [signature, srcs]);

    const L = srcs.length;
    if (L === 0) return null;

    const isOdd = L % 2 === 1;
    const totalSlides = Math.ceil(L / 2); // con 3 immagini → 2 slide (0:[0,1], 1:[2,∅])

    const nextSlide = () => {
        setDirection('left');
        setSlideIndex(prev => (prev + 1) % totalSlides);
    };

    const prevSlide = () => {
        setDirection('right');
        setSlideIndex(prev => (prev - 1 + totalSlides) % totalSlides);
    };

    // Calcolo indici della slide corrente
    const i0 = (slideIndex * 2) % L;
    const i1: number | null = (i0 + 1 < L) ? (i0 + 1) : null; // se manca, placeholder

    const currentSlideNumber = slideIndex + 1;

    const variants = {
        enter: (dir: 'left' | 'right') => ({
            x: dir === 'left' ? '100%' : '-100%',
            opacity: 0,
            position: 'absolute' as const,
            inset: 0,
        }),
        center: {
            x: 0,
            opacity: 1,
            position: 'absolute' as const,
            inset: 0,
            zIndex: 1,
        },
    exit: (dir: 'left' | 'right') => ({
        x: dir === 'left' ? '-100%' : '100%',
        opacity: 0,
        position: 'absolute' as const,
        inset: 0,
        zIndex: 0,
    }),
};

    const Cell: React.FC<{ idx: number | null; half: 'left' | 'right' | 'full' }> = ({ idx, half }) => {
        const basisClass = half === 'full' ? 'basis-full' : 'basis-1/2';

        if (idx === null) {
            // placeholder trasparente quando manca la seconda immagine (liste dispari)
            return (
                <div className={`${basisClass} shrink-0 grow-0 h-full min-w-0 relative overflow-hidden rounded-[6px]`}>
                    <div className="absolute inset-0 bg-transparent" />
                </div>
            );
        }

        const src = srcs[idx];
        const isFailed = failed[idx];
        const isLoaded = loaded[idx];

        return (
            <div className={`${basisClass} shrink-0 grow-0 h-full min-w-0 relative overflow-hidden`}>
                {!isFailed ? (
                    <img
                        src={src}
                        alt={`slide-${idx}`}
                        className="absolute inset-0 w-full h-full object-cover"
                        onError={() => {
                            setFailed(prev => {
                                if (prev[idx]) return prev;
                                const n = prev.slice(); n[idx] = true; return n;
                            });
                        }}
                        onLoad={() => {
                            setLoaded(prev => {
                                if (prev[idx]) return prev;
                                const n = prev.slice(); n[idx] = true; return n;
                            });
                        }}
                        decoding="async"
                        loading="eager"
                    />
                ) : (
                    <div className="absolute inset-0 grid place-items-center bg-neutral-800 text-white text-xs text-center px-2">
                        Immagine non disponibile
                        <div className="opacity-60 mt-1 break-all">{src}</div>
                    </div>
                )}
                {!isLoaded && !isFailed && <div className="absolute inset-0 animate-pulse bg-neutral-700/40" />}
            </div>
        );
    };

    return (
        <div className="flex flex-col justify-center items-center w-full max-w-[400px] lg:max-w-full">
            <div className="relative overflow-hidden h-[140px] sm:h-[110px] lg:h-[180px] w-full">
                <AnimatePresence initial={false} custom={direction} mode="wait">
                    <motion.div
                        key={`slide-${slideIndex}-${signature}`} // chiave unica per ogni slide
                        className={`absolute inset-0 flex ${L > 1 ? 'gap-[10px]' : ''}`}
                        custom={direction}
                        variants={variants}
                        initial="enter"
                        animate="center"
                        exit="exit"
                        transition={{ x: { type: 'spring', stiffness: 300, damping: 30 }, opacity: { duration: 0.18 } }}
                    >
                        {L === 1 ? (
                            <Cell idx={0} half="full" />
                        ) : (
                            <>
                                <Cell idx={i0} half="left" />
                                <Cell idx={i1} half="right" />
                            </>
                        )}
                    </motion.div>
                </AnimatePresence>
            </div>

            <div className="flex flex-row items-center justify-center gap-[30px] mt-[20px]">
                <button className="w-[20px] h-[20px] flex items-center justify-center" onClick={prevSlide} aria-label="Previous">
                    <img src="/images/prev-arrow-icon.svg" alt="" className="h-[15px] w-auto" />
                </button>

                <div className="flex flex-row items-center justify-center gap-[7px]">
                    {Array.from({ length: totalSlides }).map((_, index) => (
                        <div
                            key={`dot-${index}`}
                            className={`w-[7px] h-[7px] rounded-full ${index + 1 === currentSlideNumber ? 'bg-accent' : 'bg-white'}`}
                        />
                    ))}
                </div>

                <button className="w-[20px] h-[20px] flex items-center justify-center" onClick={nextSlide} aria-label="Next">
                    <img src="/images/next-arrow-icon.svg" alt="" className="h-[15px] w-auto" />
                </button>
            </div>
        </div>
    );
};

export default ImageSlider;